import { useState, useEffect, useMemo, useCallback } from "react";
import { ScrollView, type LayoutChangeEvent } from "react-native";
import { useLocalSearchParams } from "expo-router";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import SegmentedControl from "@src/components/SegmentedControl";
import { View } from "@src/components/View";
import { Text } from "@src/components/Text";
import { DataStateView } from "@src/components/screen-state";
import { EmailContactList } from "../components/EmailContactList";
import { SmsContactList } from "../components/SmsContactList";
import { ContactTab, ContactCategory, EmailContact, SmsContact } from "../types";
import { useCameraContacts, useUpdateCameraContacts } from "@src/services/api/hooks";
import { Icon } from "@src/components/Icon";
import { PressableScale } from "pressto";
import { CreateContactModal } from "../components/CreateContactModal.web";
import type { CreateContactData } from "../components/CreateContactForm";
import { useContactsStore, splitToApiContacts, UnifiedContact } from "../stores/contacts-store";

type ContactsScreenWebProps = {
  title?: string;
  category: ContactCategory;
};

export function ContactsScreen({ title = "Notification settings", category }: ContactsScreenWebProps) {
  const { theme } = useUnistyles();
  const { siteId, cameraKey } = useLocalSearchParams<{
    siteId?: string;
    cameraKey?: string;
  }>();
  const [selectedTab, setSelectedTab] = useState<ContactTab>("email");
  const [segmentedWidth, setSegmentedWidth] = useState<number | undefined>(undefined);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const hasCameraContext = Boolean(siteId && cameraKey);

  const { data: cameraContacts, isLoading, isFetching, error, refetch } = useCameraContacts(siteId, cameraKey);
  const updateCameraContactsMutation = useUpdateCameraContacts();

  const syncFromApi = useContactsStore((s) => s.syncFromApi);
  const storeKey = siteId && cameraKey ? `${siteId}:${cameraKey}` : "";
  const storeContacts = useContactsStore((s) => s.contactsByCamera[storeKey]);
  const unifiedContacts = useMemo(() => storeContacts ?? [], [storeContacts]);

  useEffect(() => {
    if (cameraContacts && siteId && cameraKey) {
      syncFromApi(siteId, cameraKey, cameraContacts);
    }
  }, [cameraContacts, siteId, cameraKey, syncFromApi]);

  const handleDeleteContact = (id: string) => {
    if (!hasCameraContext || !siteId || !cameraKey) return;

    const remaining = unifiedContacts.filter((c) => c.id !== id);
    const nextContacts = splitToApiContacts(remaining);

    updateCameraContactsMutation.mutate({
      siteId,
      cameraKey,
      contacts: nextContacts,
    });
  };

  const handleCreateContact = useCallback(
    (data: CreateContactData) => {
      if (!hasCameraContext || !siteId || !cameraKey) return;

      const fullName = `${data.firstName} ${data.lastName}`.trim();
      const contactName = fullName || data.email || "";
      const newContact: UnifiedContact = {
        id: `${contactName.toLowerCase()}|${data.email?.toLowerCase() ?? ""}|${data.phoneNumber ?? ""}`,
        name: contactName,
        email: data.email,
        phoneNumber: data.phoneNumber,
      };

      const nextContacts = splitToApiContacts([...unifiedContacts, newContact]);

      updateCameraContactsMutation.mutate(
        { siteId, cameraKey, contacts: nextContacts },
        { onSuccess: () => setIsCreateModalVisible(false) },
      );
    },
    [hasCameraContext, siteId, cameraKey, unifiedContacts, updateCameraContactsMutation],
  );

  const renderContactsContent = () => {
    const emailContacts: EmailContact[] = unifiedContacts
      .filter((c) => c.email)
      .map((c) => ({ id: c.id, email: c.email!, category }));

    const smsContacts: SmsContact[] = unifiedContacts
      .filter((c) => c.phoneNumber)
      .map((c) => ({ id: c.id, name: c.name, phone_number: c.phoneNumber!, category }));

    return (
      <View flex={1} bg="background">
        <View
          px="5xl"
          py="4xl"
          onLayout={(e: LayoutChangeEvent) => setSegmentedWidth(e.nativeEvent.layout.width - theme.spacing["5xl"] * 2)}
        >
          <SegmentedControl
            optionsLabels={[
              { title: "Email", icon: "mail" },
              { title: "SMS", icon: "messageBubbleOutline" },
            ]}
            selectedOption={selectedTab === "email" ? "Email" : "SMS"}
            onSelectOption={(option) => setSelectedTab(option === "Email" ? "email" : "sms")}
            width={segmentedWidth}
          />
        </View>

        <ScrollView contentContainerStyle={{ flexGrow: 1, paddingHorizontal: theme.spacing.xl }}>
          {selectedTab === "email" ?
            <EmailContactList contacts={emailContacts} onDeleteContact={handleDeleteContact} />
          : <SmsContactList contacts={smsContacts} onDeleteContact={handleDeleteContact} />}
          <View py="7xl">
            <PressableScale onPress={() => setIsCreateModalVisible(true)}>
              <View style={styles.button}>
                <Text textStyle="sm/regular">Create new contact</Text>
                <Icon name="plus" size={theme.spacing["5xl"]} color={theme.colors.iconPrimary} />
              </View>
            </PressableScale>
          </View>
        </ScrollView>
      </View>
    );
  };

  return (
    <>
      <View flex={1}>
        <View px="5xl" py="4xl">
          <Text textStyle="lg/semibold" color="text">
            {title}
          </Text>
        </View>
        <DataStateView
          data={cameraContacts}
          isLoading={isLoading}
          isFetching={isFetching}
          error={error as Error | null}
          refetch={refetch}
          loadingMessage="Loading contacts..."
          errorTitle="Unable to load contacts"
          emptyState={{
            icon: "users",
            title: "No contacts yet",
            message: "Click the + button to add an email or SMS contact.",
          }}
        >
          {() => renderContactsContent()}
        </DataStateView>
      </View>
      <CreateContactModal
        visible={isCreateModalVisible}
        mode={selectedTab}
        onSave={handleCreateContact}
        onClose={() => setIsCreateModalVisible(false)}
        isSaving={updateCameraContactsMutation.isPending}
      />
    </>
  );
}

const styles = StyleSheet.create((theme) => ({
  button: {
    height: 44,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: theme.spacing.xl,
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.interactive.secondary.default_border,
    backgroundColor: theme.colors.interactive.secondary.background_secondary,
  },
}));
